import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api, ApiError, type ApiShareLookup } from "../lib/api";
import { usePages } from "../hooks/usePages";
import { ReviewSurface } from "../components/ReviewSurface";
import { PagesSidebar } from "../components/PagesSidebar";
import { ReviewerHeader } from "../components/ReviewerHeader";
import { Spinner } from "../components/Loading";
import { SidebarToggle } from "../components/SidebarToggle";

const NUDGE_SESSION_KEY = "clarity_share_signup_nudge_shown";
// Shared with the owner project view so a collaborator who collapsed the
// pages list keeps that preference across owner/share contexts on the
// same machine.
const PAGES_HIDDEN_STORAGE_KEY = "clarity_pages_hidden";

function loadPagesHidden(): boolean {
  try {
    return localStorage.getItem(PAGES_HIDDEN_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function savePagesHidden(hidden: boolean) {
  try {
    if (hidden) localStorage.setItem(PAGES_HIDDEN_STORAGE_KEY, "1");
    else localStorage.removeItem(PAGES_HIDDEN_STORAGE_KEY);
  } catch {
    // ignore — same fallback rationale as ProjectDetailPage
  }
}

type LoadState =
  | { status: "loading" }
  | { status: "ready"; data: ApiShareLookup }
  | { status: "missing" }
  | { status: "error" };

export function SharePage() {
  const { token } = useParams<{ token: string }>();
  const [state, setState] = useState<LoadState>({ status: "loading" });
  const [pagesHidden, setPagesHiddenState] = useState<boolean>(loadPagesHidden);
  const [currentPath, setCurrentPath] = useState<string>("");

  const setPagesHidden = useCallback((next: boolean) => {
    setPagesHiddenState(next);
    savePagesHidden(next);
  }, []);
  // Bumped once per browsing session after a reviewer posts their first
  // comment — the ReviewerHeader watches this and pops its sign-up panel.
  const [signupPulse, setSignupPulse] = useState<number | undefined>(undefined);

  const onReviewerCommentPosted = useCallback(() => {
    try {
      if (sessionStorage.getItem(NUDGE_SESSION_KEY)) return;
      sessionStorage.setItem(NUDGE_SESSION_KEY, "1");
    } catch {
      // SessionStorage can be disabled (private mode, embedded iframe with
      // restricted storage). Skip the nudge rather than spamming on every
      // post.
      return;
    }
    setSignupPulse((n) => (n ?? 0) + 1);
  }, []);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    api
      .shareLookup(token)
      .then((data) => {
        if (!cancelled) setState({ status: "ready", data });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) setState({ status: "missing" });
        else setState({ status: "error" });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // Same stable-hook-order discipline as ProjectDetailPage: call before
  // any early return. An empty shareToken means the hook fetches nothing
  // until we have a real token.
  const pages = usePages(
    state.status === "ready"
      ? { mode: "share", shareToken: token ?? "" }
      : { mode: "share", shareToken: "" },
  );

  if (state.status === "loading")
    return (
      <ReviewerShell shareToken={token ?? ""} projectName={null}>
        <div className="mx-auto flex max-w-md flex-col items-center gap-3 px-6 py-20 text-center text-neutral-600">
          <Spinner size={20} />
          <span>Loading review…</span>
        </div>
      </ReviewerShell>
    );
  if (state.status === "missing")
    return (
      <ReviewerShell shareToken={token ?? ""} projectName={null}>
        <Centered>This share link is invalid or the project has been archived.</Centered>
      </ReviewerShell>
    );
  if (state.status === "error")
    return (
      <ReviewerShell shareToken={token ?? ""} projectName={null}>
        <Centered>Something went wrong loading this review.</Centered>
      </ReviewerShell>
    );

  const project = state.data.project;
  return (
    <>
      <ReviewerHeader
        projectName={project.name}
        shareToken={token ?? ""}
        pulseToken={signupPulse}
      />
      <section className="flex h-[calc(100vh-3.5rem)]">
      {!pagesHidden ? (
        <div className="flex w-72 shrink-0 flex-col overflow-y-auto border-r border-neutral-200 bg-neutral-50 px-5 py-6">
          <div className="flex items-start justify-between gap-2">
            <h1 className="truncate text-base font-semibold">{project.name}</h1>
            <SidebarToggle
              hidden={false}
              onToggle={() => setPagesHidden(true)}
              variant="edge"
            />
          </div>
          <a
            href={project.targetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-1 block truncate text-xs text-neutral-500 hover:underline"
          >
            {project.targetUrl}
          </a>
          <div className="mt-6 text-xs text-neutral-500">
            Click anywhere on the page to leave a comment. You’ll only be asked
            for your name and email once.
          </div>

          <div className="mt-6">
            <PagesSidebar
              pages={pages.pages}
              activePath={currentPath || null}
              mode="share"
              onSelect={(page) => setCurrentPath(page.path)}
            />
          </div>
        </div>
      ) : null}
      <div className="relative min-w-0 flex-1">
        {pagesHidden ? (
          <SidebarToggle
            hidden={true}
            onToggle={() => setPagesHidden(false)}
            variant="floating"
          />
        ) : null}
        <ReviewSurface
          mode="share"
          projectId={project.id}
          shareToken={token ?? ""}
          defaultIdentity={null}
          pages={pages.pages}
          currentPath={currentPath}
          onSetCurrentPath={setCurrentPath}
          onCommentsChanged={pages.reload}
          onReviewerCommentPosted={onReviewerCommentPosted}
        />
      </div>
      </section>
    </>
  );
}

function ReviewerShell({
  shareToken,
  projectName,
  children,
}: {
  shareToken: string;
  projectName: string | null;
  children: React.ReactNode;
}) {
  return (
    <>
      <ReviewerHeader projectName={projectName} shareToken={shareToken} />
      <section className="h-[calc(100vh-3.5rem)]">{children}</section>
    </>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto max-w-md px-6 py-20 text-center text-neutral-600">{children}</div>
  );
}
